import { useMemo } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";

import { useActivationSet } from "../../../../hooks/useActivationSet";
import type { IChartDataItem } from "./types";

const COMPACT_ITEM_COUNT = 5;
const OTHERS_COLOR = colorsPalettes.carbon[100];

export const useCompactChartData = (fullData: IChartDataItem[] | undefined) => {
    const { translate } = useTranslate();
    const { activationSet } = useActivationSet();
    const primaryDomain = activationSet?.primaryDomain;

    return useMemo(() => {
        if (!fullData || fullData.length <= COMPACT_ITEM_COUNT) {
            return { compactData: fullData ?? [], hasOthers: false };
        }

        const isPrimaryInTop = fullData.slice(0, COMPACT_ITEM_COUNT - 1).some((item) => item.name === primaryDomain);
        const maxVisibleCount = isPrimaryInTop ? COMPACT_ITEM_COUNT - 1 : COMPACT_ITEM_COUNT - 2;

        const visibleItems: IChartDataItem[] = [];
        let othersShare = 0;
        let othersValue = 0;

        for (const item of fullData) {
            if (item.name === primaryDomain && !isPrimaryInTop) {
                visibleItems.push(item);
                continue;
            }

            if (visibleItems.length < maxVisibleCount) {
                visibleItems.push(item);
                continue;
            }

            othersShare += item.y;
            othersValue += item.value;
        }

        const othersItem: IChartDataItem = {
            id: "others",
            color: OTHERS_COLOR,
            name: translate("activation.alerts.metrics.others"),
            visible: true,
            y: othersShare,
            value: othersValue,
            isOthers: true,
        };

        visibleItems.push(othersItem);

        return { compactData: visibleItems, hasOthers: true };
    }, [fullData, primaryDomain, translate]);
};
